import { MAX_TRANSCRIPT_TURNS, type TranscriptTurn } from '@acs/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { BusinessDoc } from '../../shared/api/converters.js';
import {
  execTool,
  finalizeDemoCall,
  finalizeTestCall,
  startTestSession,
  type TestSessionStart,
} from '../../shared/api/testCall.js';
import { BrowserCall, browserClock } from './browserCall.js';
import { createWaveAudioContext } from './CallWave.js';
import { MediaSocketCall } from './mediaSocketCall.js';

export type CallStatus = 'idle' | 'connecting' | 'live' | 'ended' | 'error';

export interface CallTurn {
  role: 'assistant' | 'you' | 'tool';
  text: string;
}

/** Persisted transcript-turn schema caps (mirror {@link TranscriptToolSchema}). */
const TOOL_ARGS_MAX = 2_000;
const TOOL_RESULT_MAX = 4_000;

/** Small buffer between the goodbye audio draining and tearing the call down. */
const POST_GOODBYE_GRACE_MS = 500;

/** The gateway's transcript as the page shows it: what was heard, tools as one line (not in a demo). */
export function visibleTurns(server: readonly (TranscriptTurn | undefined)[], demo: boolean): CallTurn[] {
  const out: CallTurn[] = [];
  for (const turn of server) {
    if (!turn || turn.suppressed || !turn.text?.trim()) continue;
    if (turn.role === 'tool') {
      if (!demo && turn.tool) out.push({ role: 'tool', text: `${turn.tool.name}(${turn.tool.args}) → ${turn.tool.result}` });
    } else out.push({ role: turn.role === 'customer' ? 'you' : 'assistant', text: turn.text.trim() });
  }
  return out;
}

/** JSON-serialize a tool payload, never throwing, clipped to a length cap. */
function clipJson(value: unknown, max: number): string {
  let s: string;
  try {
    s = JSON.stringify(value ?? {});
  } catch {
    s = '"[unserializable]"';
  }
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * One browser call to the AI receptionist, on the SAME engine as a phone call
 * (2026-10-15). The gateway decides which:
 *
 * - **Live** (`mediaUrl` in the start response): the browser streams μ-law to
 *   the gateway's media socket like a phone carrier (`MediaSocketCall`) and
 *   the gateway runs the phone's own session — tools, read-backs, the record.
 *   The page only shows the transcript the gateway sends.
 * - **Realtime**: audio is browser ↔ provider over WebRTC, the call created BY
 *   THE GATEWAY from this browser's SDP offer (doc 33 W10), driven by the same
 *   coordinator the phone runs (`BrowserCall`).
 *
 * `demo` is setup's "Try it" call: a throwaway conversation that writes
 * nothing, and the one that also exposes both audio streams and an audio
 * context (made inside the Start click) for the voice-driven wave.
 */
export function useBrowserCall({
  business,
  demo,
  simulatedFrom = '',
  onTool,
}: {
  business: BusinessDoc;
  demo: boolean;
  /** Settings' simulated caller ID (doc 14 D2); ignored by a demo. */
  simulatedFrom?: string;
  /** Every tool call as it completes — the Try step reads the booking from it. */
  onTool?: (name: string, args: unknown, result: unknown) => void;
}) {
  const [status, setStatus] = useState<CallStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<CallTurn[]>([]);
  const [session, setSession] = useState<TestSessionStart | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  // When the line went live and when it ended, for the call timer.
  const [liveAt, setLiveAt] = useState<number | null>(null);
  const [endedAt, setEndedAt] = useState<number | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const callRef = useRef<BrowserCall | null>(null);
  const socketRef = useRef<MediaSocketCall | null>(null);
  /** This call ran on the media socket: the gateway finalises it, not us. */
  const viaSocketRef = useRef(false);
  const businessIdRef = useRef<string>('');
  const sessionIdRef = useRef<string>('');
  const startedAtRef = useRef<number>(0);
  const transcriptRef = useRef<TranscriptTurn[]>([]);
  const errorRef = useRef<string | null>(null);
  const finalizedRef = useRef<boolean>(false);
  const timedOutRef = useRef(false);
  const deadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onToolRef = useRef(onTool);
  onToolRef.current = onTool;

  const elapsed = useCallback(() => Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000)), []);

  const addTurn = useCallback(
    (role: CallTurn['role'], text: string) => {
      if (!text?.trim()) return;
      setTurns((prev) => [...prev, { role, text }]);
      if ((role === 'assistant' || role === 'you') && transcriptRef.current.length < MAX_TRANSCRIPT_TURNS) {
        transcriptRef.current.push({ role: role === 'you' ? 'customer' : 'assistant', text, t: elapsed() });
      }
    },
    [elapsed],
  );

  // Persist the finished session (best-effort, exactly once). The OUTCOME is
  // the server's: it reads the appointments this session actually holds.
  const finalizeCall = useCallback(() => {
    if (finalizedRef.current || !startedAtRef.current || !sessionIdRef.current) return;
    // A media-socket call is finalised by the gateway's own session.
    if (viaSocketRef.current) return;
    finalizedRef.current = true;
    const transcript = transcriptRef.current.slice(-MAX_TRANSCRIPT_TURNS);
    if (demo) {
      void finalizeDemoCall({
        businessId: businessIdRef.current,
        sessionId: sessionIdRef.current,
        startedAt: startedAtRef.current,
        endedBy: timedOutRef.current ? 'timeout' : errorRef.current ? 'error' : 'user',
        transcript,
      }).catch(() => undefined);
      return;
    }
    void finalizeTestCall({
      businessId: businessIdRef.current,
      sessionId: sessionIdRef.current,
      transcript,
      error: errorRef.current,
    }).catch(() => undefined);
  }, [demo]);

  const stop = useCallback(() => {
    if (deadlineRef.current) clearTimeout(deadlineRef.current);
    deadlineRef.current = null;
    callRef.current?.close();
    callRef.current = null;
    finalizeCall();
    socketRef.current?.hangup();
    socketRef.current = null;
    dcRef.current?.close();
    pcRef.current?.getSenders().forEach((s) => s.track?.stop());
    pcRef.current?.close();
    micRef.current?.getTracks().forEach((tr) => tr.stop());
    dcRef.current = null;
    pcRef.current = null;
    micRef.current = null;
    setRemoteStream(null);
    setMicStream(null);
    setAudioContext(null);
    setEndedAt((at) => at ?? Date.now());
    setStatus((s) => (s === 'error' || s === 'idle' ? s : 'ended'));
  }, [finalizeCall]);

  // Navigating away ends the call (and the server ends the provider session).
  useEffect(() => stop, [stop]);
  // Each call's audio context is released when the call drops it or we unmount.
  useEffect(() => () => void audioContext?.close().catch(() => undefined), [audioContext]);

  const start = useCallback(async () => {
    setError(null);
    setTurns([]);
    setLiveAt(null);
    setEndedAt(null);
    setStatus('connecting');
    // Before the first await, while this is still the owner's click: Safari
    // keeps a context made later suspended. The media-socket call plays and
    // captures through it; a demo's wave analyses it.
    const context = createWaveAudioContext();
    setAudioContext(context);
    try {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.ontrack = (e) => {
        const [stream] = e.streams;
        if (stream && audioRef.current) audioRef.current.srcObject = stream;
        if (stream) setRemoteStream(stream);
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') stop();
      };
      const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      micRef.current = mic;
      setMicStream(mic);
      mic.getTracks().forEach((tr) => pc.addTrack(tr, mic));
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sessionIdRef.current = crypto.randomUUID();
      transcriptRef.current = [];
      errorRef.current = null;
      finalizedRef.current = false;
      timedOutRef.current = false;
      viaSocketRef.current = false;
      const started = await startTestSession({
        businessId: business.id,
        sessionId: sessionIdRef.current,
        sdp: offer.sdp ?? '',
        from: demo ? null : simulatedFrom.trim() || null,
        demo,
      });
      businessIdRef.current = started.businessId;
      startedAtRef.current = Date.now();
      setSession(started);

      if (started.mediaUrl) {
        // Live: the phone's own session, reached over the gateway media socket.
        pc.close();
        pcRef.current = null;
        dcRef.current = null;
        viaSocketRef.current = true;
        if (!context) throw new Error('This browser cannot play call audio (no Web Audio).');
        const heard: (TranscriptTurn | undefined)[] = [];
        const socket = new MediaSocketCall({
          url: started.mediaUrl,
          streamSid: `browser-${started.sessionId}`,
          context,
          mic,
          output: audioRef.current,
          onOpen: () => {
            setStatus('live');
            setLiveAt(Date.now());
            deadlineRef.current = setTimeout(() => {
              timedOutRef.current = true;
              stop();
            }, Math.max(0, started.deadlineAt - Date.now()));
          },
          onTurn: (index, turn) => {
            heard[index] = turn;
            setTurns(visibleTurns(heard, demo));
          },
          onTool: (name, args, result) => onToolRef.current?.(name, args, result),
          onClose: ({ code, reason, started: wasLive }) => {
            if (!wasLive) {
              const message = `media socket closed (${code}) ${reason}`.trim();
              errorRef.current = message;
              setError(message);
              setStatus('error');
            }
            socketRef.current = null;
            stop();
          },
        });
        socketRef.current = socket;
        setRemoteStream(socket.remoteStream);
        await socket.start();
        return;
      }

      const call = new BrowserCall({
        send: (event) => dcRef.current?.readyState === 'open' && dcRef.current.send(JSON.stringify(event)),
        execTool: (name, callId, args, events) =>
          execTool({ businessId: started.businessId, sessionId: started.sessionId, name, callId, args, events, demo }),
        clock: browserClock,
        policy: started.policy,
        greetingToolChoice: started.greetingToolChoice,
        onTranscript: (role, text) => addTurn(role, text),
        onToolTurn: (name, args, result) => {
          const argsJson = clipJson(args, TOOL_ARGS_MAX);
          const resultJson = clipJson(result, TOOL_RESULT_MAX);
          const r = result as { ok?: boolean };
          if (transcriptRef.current.length < MAX_TRANSCRIPT_TURNS) {
            transcriptRef.current.push({
              role: 'tool', text: name, t: elapsed(),
              tool: { name, args: argsJson, result: resultJson, ok: typeof r?.ok === 'boolean' ? r.ok : null },
            });
          }
          onToolRef.current?.(name, args, result);
          // A demo shows the owner their receptionist, not our wire protocol.
          if (!demo) addTurn('tool', `${name}(${argsJson}) → ${resultJson}`);
        },
        onEnd: (reason) => {
          if (reason === 'model_unavailable') errorRef.current = 'model_unavailable';
          setTimeout(stop, reason === 'goodbye' ? POST_GOODBYE_GRACE_MS : 0);
        },
      });
      callRef.current = call;
      dc.onopen = () => {
        setStatus('live');
        setLiveAt(Date.now());
        call.begin();
        // The server hangs up at the deadline regardless; this just ends the
        // UI at the same moment.
        deadlineRef.current = setTimeout(() => {
          timedOutRef.current = true;
          stop();
        }, Math.max(0, started.deadlineAt - Date.now()));
      };
      dc.onmessage = (e) => void call.handle(e.data as string);
      await pc.setRemoteDescription({ type: 'answer', sdp: started.sdp });
    } catch (e) {
      errorRef.current = (e as Error).message;
      setError((e as Error).message);
      setStatus('error');
      stop();
    }
  }, [addTurn, business, demo, elapsed, simulatedFrom, stop]);

  return {
    status,
    active: status === 'connecting' || status === 'live',
    error,
    turns,
    session,
    remoteStream,
    micStream,
    audioContext,
    liveAt,
    endedAt,
    audioRef,
    start,
    stop,
  };
}
